import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { publisherResultImportsApi } from '@/api/publisher-result-imports';
import {
    FLOATING_HELP_EDGE_GAP_PX,
    FLOATING_HELP_WIDTH_PX,
} from '@/components/common/HelpMenuButton';
import {
    ArrowDown2,
    Cross,
    Danger,
    Spinner,
    TickCircle,
    Warning2,
    type IconComponent,
} from '@/components/icons';
import { useTestBlockSelection } from '@/components/preview/block-selection/TestBlockSelectionContext';
import {
    getImportsDockSignature,
    getImportsDockSummary,
    STATUS_META,
    type ImportsDockSummary,
} from '@/components/publisher-results/import-status';
import { Button } from '@/components/ui/button';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import {
    usePendingResultsReview,
    usePublisherResultImportModal,
} from '@/components/wizard/PendingResultsReviewModal';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { isPublisherResultImportActive } from '@/lib/publisher-result-imports';
import { cn } from '@/lib/utils';
import { useReportWizard } from '../context/ReportWizardContext';
import { useReportPublisherImports } from '../hooks/useReportPublisherImports';
import type { PublisherResultImport } from '@/types/publisher-result-import';

/** Clears the help pill, which owns the same bottom gutter on this page. */
const HELP_BUTTON_CLEARANCE_PX = FLOATING_HELP_EDGE_GAP_PX + FLOATING_HELP_WIDTH_PX + 12;

/**
 * Dismissed by the psychologist, remembered per report.
 *
 * The signature is the batch it was dismissed against — see
 * `getImportsDockSignature`, which decides when the dot earns its way back.
 */
type DockDismissal = { signature: string } | null;

const TONE_STYLES: Record<
    ImportsDockSummary['tone'],
    {
        icon: IconComponent;
        iconClass: string;
        /** Ink-weight fill: the count sits *on* this one, so it has to carry text. */
        badgeClass: string;
        spin?: boolean;
    }
> = {
    info: {
        icon: Spinner,
        iconClass: 'text-info',
        badgeClass: 'bg-info text-info-foreground',
        spin: true,
    },
    success: {
        icon: TickCircle,
        iconClass: 'text-success',
        badgeClass: 'bg-success text-success-foreground',
    },
    warning: {
        icon: Warning2,
        iconClass: 'text-warning-strong',
        badgeClass: 'bg-warning-strong text-white',
    },
    destructive: {
        icon: Danger,
        iconClass: 'text-destructive-strong',
        badgeClass: 'bg-destructive-strong text-white',
    },
};

const rowIcon = (record: PublisherResultImport) => {
    if (record.status === 'ready_for_approval')
        return <TickCircle className="size-4 shrink-0 text-success" />;
    if (record.status === 'failed') return <Danger className="size-4 shrink-0 text-destructive" />;
    if (record.status === 'duplicate_warning')
        return <Warning2 className="size-4 shrink-0 text-warning-strong" />;

    return <Spinner className="size-4 shrink-0 animate-spin text-info" />;
};

/**
 * Tracks publisher-result imports from anywhere in the report editor.
 *
 * The import modal tells the psychologist to close it — the work continues
 * without them — and until now that was the end of the thread: the only way
 * back was the patient page. This dock keeps the thread inside the editor, and
 * hands it back as an action the moment extraction finishes.
 *
 * It rests as a dot in the corner and only becomes a card when asked: the
 * editor belongs to the laudo, not to the import. Clicking the dot opens the
 * file list; clicking the header puts it away again. The X dismisses the batch
 * outright — never cancels it — and even that is not final while work is
 * happening: the dot returns whenever the batch changes (see
 * `getImportsDockSignature`), because "pronto para revisar" is an action, not a
 * notice. Once nothing is moving the signature stops changing, so a dismissed
 * failure — terminal server-side — stays dismissed.
 */
export function ReportImportsDock({
    rightSidebarWidth,
    isMobile,
}: {
    rightSidebarWidth: number;
    isMobile: boolean;
}) {
    const queryClient = useQueryClient();
    const {
        formData,
        reportId,
        isReadOnly,
        pendingPublisherResultApprovalImports,
        publisherResultApprovalProcessingCount,
    } = useReportWizard();
    const publisherResultImportsFeature = useFeatureFlag('publisherResultImports.reviewFlow');
    const { openPendingResultsReview } = usePendingResultsReview(formData);
    const { openPublisherResultImport } = usePublisherResultImportModal(formData);
    const { enabled: selectionEnabled, selectedIds, clipboardState } = useTestBlockSelection();

    // Closed by default: the dot is the resting state, the card is the ask.
    const [open, setOpen] = useState(false);
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    const lastPhaseRef = useRef<ImportsDockSummary['phase'] | null>(null);

    const patientId = formData.patient?.id;
    const activeReportId = reportId || formData.id || null;
    const readyHere = pendingPublisherResultApprovalImports.filter(
        (record) => record.source_report_id === activeReportId,
    );

    const records = useReportPublisherImports({
        patientId,
        reportId: activeReportId,
        hasSignal: publisherResultApprovalProcessingCount > 0 || readyHere.length > 0,
        enabled: publisherResultImportsFeature.enabled && !isReadOnly,
    });

    const summary = getImportsDockSummary(records);
    const phase = summary?.phase ?? null;
    const signature = getImportsDockSignature(records);
    const [dismissal, setDismissal] = useLocalStorage<DockDismissal>(
        `imports_dock_dismissed_${activeReportId ?? 'novo'}`,
        null,
    );

    // A dismissal only covers the batch it was made against; once the batch
    // moves on, drop it rather than carry a stale key around.
    useEffect(() => {
        if (dismissal && dismissal.signature !== signature) setDismissal(null);
    });

    // Coming out of `processing` is the one transition worth interrupting for:
    // something either needs a decision or is ready to go into the report.
    useEffect(() => {
        if (phase && phase !== 'processing' && lastPhaseRef.current === 'processing') {
            setOpen(true);
        }

        lastPhaseRef.current = phase;
    }, [phase]);

    if (!summary) return null;

    if (dismissal?.signature === signature) return null;

    const hasActiveImport = records.some(isPublisherResultImportActive);

    const handleDismiss = () => {
        setOpen(false);
        setDismissal({ signature });
    };

    const handleCancel = async (record: PublisherResultImport) => {
        setCancellingId(record.id);

        try {
            await publisherResultImportsApi.cancel(record.id);
            await queryClient.invalidateQueries({ queryKey: ['publisher-result-imports'] });
            // The row just disappears from the list, so say why.
            toast.success('Importação cancelada.');
        } catch {
            toast.error('Não foi possível cancelar a importação.');
        } finally {
            setCancellingId(null);
        }
    };

    const handleCtaClick = () => {
        if (!summary.cta) return;

        setOpen(false);

        if (summary.cta.action === 'review') {
            openPendingResultsReview();
            return;
        }

        if (summary.cta.action === 'review-duplicate') {
            const duplicate = records.find((record) => record.status === 'duplicate_warning');
            openPublisherResultImport({
                batchId: duplicate?.batch_id,
                importId: duplicate?.id,
            });
            return;
        }

        openPublisherResultImport();
    };

    /**
     * A row only carries its own action when the header is busy speaking for
     * somebody else — a file that is ready inside a batch where another one
     * failed would otherwise have no way through.
     */
    const rowAction = (record: PublisherResultImport) => {
        if (record.status === 'ready_for_approval' && summary.phase !== 'ready') {
            return { label: 'Revisar', onClick: () => openPendingResultsReview() };
        }

        if (record.status === 'duplicate_warning' && summary.cta?.action !== 'review-duplicate') {
            return {
                label: 'Revisar',
                onClick: () =>
                    openPublisherResultImport({ batchId: record.batch_id, importId: record.id }),
            };
        }

        return null;
    };

    const tone = TONE_STYLES[summary.tone];
    const ToneIcon = tone.icon;
    const hideForBlockSelection =
        selectionEnabled && (selectedIds.size > 0 || clipboardState !== null);

    return (
        <aside
            aria-label="Importações deste laudo"
            className={cn(
                // Same bottom gutter as the help button and the zoom pill, so
                // the card grows straight up out of the dot it replaced.
                'fixed bottom-6 z-40 transition-opacity duration-200',
                open && 'w-80 max-w-[calc(100vw-2rem)]',
                isMobile && 'bottom-4 left-4',
                hideForBlockSelection && 'pointer-events-none opacity-0',
            )}
            style={
                isMobile
                    ? undefined
                    : {
                          right: rightSidebarWidth + HELP_BUTTON_CLEARANCE_PX,
                      }
            }
        >
            {!open ? (
                <TooltipIconButton
                    label="Importações deste laudo"
                    tooltip={
                        summary.phase === 'processing'
                            ? `${summary.shortTitle} · ${summary.percent}%`
                            : summary.shortTitle
                    }
                    side="top"
                    onClick={() => setOpen(true)}
                    className="relative size-10 rounded-full border border-border bg-card shadow-elevation-3 hover:bg-surface-muted"
                >
                    <ToneIcon
                        className={cn('size-4', tone.iconClass, tone.spin && 'animate-spin')}
                    />
                    {records.length > 1 && (
                        <span
                            className={cn(
                                'absolute -right-1 -top-1 grid size-4 place-items-center rounded-full border-2 border-card text-[10px] font-bold leading-none',
                                tone.badgeClass,
                            )}
                        >
                            {records.length > 9 ? '9' : records.length}
                        </span>
                    )}
                </TooltipIconButton>
            ) : (
                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-elevation-2">
                    <div className="flex items-center pr-1.5">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setOpen(false)}
                            aria-expanded
                            aria-label="Recolher importações"
                            className="flex h-auto min-w-0 flex-1 items-center justify-start gap-2.5 rounded-none px-3 py-2.5 hover:bg-surface-muted"
                        >
                            <ToneIcon
                                className={cn(
                                    'size-4 shrink-0',
                                    tone.iconClass,
                                    tone.spin && 'animate-spin',
                                )}
                            />
                            <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-foreground">
                                {summary.title}
                            </span>
                            {summary.phase === 'processing' && (
                                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                    {summary.percent}%
                                </span>
                            )}
                            <ArrowDown2 className="size-4 shrink-0 text-muted-foreground" />
                        </Button>
                        <TooltipIconButton
                            label={
                                hasActiveImport
                                    ? 'Dispensar. A importação continua'
                                    : 'Dispensar importações'
                            }
                            onClick={handleDismiss}
                            className="shrink-0 text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                            <Cross className="size-4" />
                        </TooltipIconButton>
                    </div>

                    <div className="border-t border-border/70 px-3 pb-3 pt-1">
                        {/* Scrolls so a batch of eight files cannot push the dock
                            off the top of the screen; the CTA stays outside it. */}
                        <ul className="max-h-56 overflow-y-auto">
                            {records.map((record) => {
                                const action = rowAction(record);

                                return (
                                    <li
                                        key={record.id}
                                        className="flex items-center gap-2.5 border-t border-border/70 py-2 first:border-t-0"
                                    >
                                        {rowIcon(record)}
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-xs text-foreground">
                                                {record.original_filename}
                                            </p>
                                            <p
                                                className={cn(
                                                    'text-xs leading-4',
                                                    record.status === 'failed'
                                                        ? 'text-destructive-strong'
                                                        : 'text-muted-foreground',
                                                )}
                                            >
                                                {record.status === 'failed'
                                                    ? record.safe_error_message ||
                                                      STATUS_META.failed.description
                                                    : STATUS_META[record.status].label}
                                            </p>
                                        </div>
                                        {isPublisherResultImportActive(record) && (
                                            <TooltipIconButton
                                                label="Cancelar importação"
                                                onClick={() => void handleCancel(record)}
                                                disabled={cancellingId === record.id}
                                                className="shrink-0 text-muted-foreground hover:bg-accent hover:text-foreground"
                                            >
                                                {cancellingId === record.id ? (
                                                    <Spinner className="size-4 animate-spin" />
                                                ) : (
                                                    <Cross className="size-4" />
                                                )}
                                            </TooltipIconButton>
                                        )}
                                        {action && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-7 shrink-0 px-2 text-xs"
                                                onClick={() => {
                                                    setOpen(false);
                                                    action.onClick();
                                                }}
                                            >
                                                {action.label}
                                            </Button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>

                        {summary.cta && (
                            <Button size="sm" className="mt-2 w-full" onClick={handleCtaClick}>
                                {summary.cta.label}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </aside>
    );
}
